import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import GroundMaterial from '../Materials/Ground.js'
import ShadowMaterial from '../Materials/Shadow.js'
import { getMachinePortLayout, MACHINE_TYPES, TRAY_SIZES } from '../Game/definitions.js'

const randomFrom = (_seed) =>
{
    let value = _seed
    return () =>
    {
        value = Math.sin(value * 12.9898) * 43758.5453
        return value - Math.floor(value)
    }
}

export default class ModelFactory
{
    constructor(_options)
    {
        this.resources = _options.resources
        this.materials = _options.materials.shades.items
        for(const material of Object.values(this.materials))
            material.uniforms.uRevealProgress.value = 1
        this.prepareOriginalModels()
    }

    mesh(_geometry, _material, _position = [0, 0, 0], _rotation = [0, 0, 0])
    {
        const mesh = new THREE.Mesh(_geometry, _material)
        mesh.position.set(..._position)
        mesh.rotation.set(..._rotation)
        return mesh
    }

    box(_width, _height, _depth, _radius = 0.06, _segments = 2)
    {
        return new RoundedBoxGeometry(_width, _height, _depth, _segments, Math.min(_radius, _width * 0.25, _height * 0.25, _depth * 0.25))
    }

    capsule(_radius, _length, _segments = 20)
    {
        const geometry = new THREE.CapsuleGeometry(_radius, _length, 8, _segments)
        geometry.rotateX(Math.PI * 0.5)
        return geometry
    }

    cylinder(_top, _bottom, _height, _segments = 12)
    {
        const geometry = new THREE.CylinderGeometry(_top, _bottom, _height, _segments)
        geometry.rotateX(Math.PI * 0.5)
        return geometry
    }

    prepareOriginalModels()
    {
        const scene = this.resources.items.introStaticBase.scene
        scene.updateMatrixWorld(true)
        let rock
        let trunk
        let leaves
        scene.traverse((_child) =>
        {
            if(!_child.isMesh)
                return
            if(!rock && /^shadeWhite_001/.test(_child.name))
                rock = _child
            if(!trunk && /^shadeBrown/.test(_child.name))
                trunk = _child
            if(!leaves && /^shadeGreen/.test(_child.name))
                leaves = _child
        })

        this.rockGeometry = rock ? this.bakeGeometry(rock) : new THREE.DodecahedronGeometry(0.8, 0)
        this.normalizeGeometry(this.rockGeometry)
        this.treeParts = []
        if(trunk && leaves)
        {
            this.treeParts = [
                { geometry: this.bakeGeometry(trunk), material: this.materials.brown },
                { geometry: this.bakeGeometry(leaves), material: this.materials.green }
            ]
            const box = new THREE.Box3()
            for(const part of this.treeParts)
            {
                part.geometry.computeBoundingBox()
                box.union(part.geometry.boundingBox)
            }
            const center = box.getCenter(new THREE.Vector3())
            for(const part of this.treeParts)
                part.geometry.translate(- center.x, - center.y, - box.min.z)
        }

        const informationScene = this.resources.items.informationStaticBase.scene
        informationScene.updateMatrixWorld(true)
        let pin
        informationScene.traverse((_child) =>
        {
            if(!pin && _child.isMesh && /^shadeRed001/.test(_child.name))
                pin = _child
        })
        this.pinGeometry = pin ? this.bakeGeometry(pin) : null
        if(this.pinGeometry)
            this.normalizeGeometry(this.pinGeometry)
    }

    bakeGeometry(_mesh)
    {
        const geometry = _mesh.geometry.clone()
        geometry.applyMatrix4(_mesh.matrixWorld)
        return geometry
    }

    normalizeGeometry(_geometry)
    {
        _geometry.computeBoundingBox()
        const box = _geometry.boundingBox
        const center = box.getCenter(new THREE.Vector3())
        _geometry.translate(- center.x, - center.y, - box.min.z)
        const size = box.getSize(new THREE.Vector3())
        const scale = 1.6 / Math.max(size.x, size.y)
        _geometry.scale(scale, scale, scale)
    }

    createShadow(_width = 2, _height = _width)
    {
        const material = new ShadowMaterial()
        material.uniforms.uColor.value = new THREE.Color('#d04500')
        material.uniforms.uAlpha.value = 0.38
        material.uniforms.uFadeRadius.value = 0.35
        const shadow = this.mesh(new THREE.PlaneGeometry(_width, _height), material, [0.35, 0.4, 0.025])
        shadow.renderOrder = 1
        return shadow
    }

    createGround()
    {
        const group = new THREE.Group()
        const top = new GroundMaterial()
        const ground = this.mesh(new THREE.PlaneGeometry(2, 2), top)
        ground.frustumCulled = false
        group.add(ground)
        return group
    }

    createTree()
    {
        const group = new THREE.Group()
        if(this.treeParts.length)
        {
            for(const part of this.treeParts)
                group.add(this.mesh(part.geometry, part.material))
        }
        else
        {
            group.add(this.mesh(this.cylinder(0.15, 0.2, 1.5, 7), this.materials.brown, [0, 0, 0.75]))
            group.add(this.mesh(this.box(1.4, 1.2, 2.2, 0.15), this.materials.green, [0, 0, 2.3]))
        }
        return group
    }

    createDeposit(_deposit)
    {
        const group = new THREE.Group()
        group.position.set(_deposit.x, _deposit.y, 0)
        if(_deposit.tree)
        {
            const tree = this.createTree()
            const stump = this.mesh(this.cylinder(0.2, 0.25, 0.35, 8), this.materials.brown, [0, 0, 0.18])
            stump.visible = false
            group.add(tree)
            group.add(stump)
            group.userData.tree = tree
            group.userData.stump = stump
            return group
        }

        const random = randomFrom(_deposit.x * 13 + _deposit.y * 31 + 100)
        const baseMaterial = _deposit.resource === 'coal' ? this.materials.black : _deposit.resource === 'ironOre' ? this.materials.red : _deposit.resource === 'copperOre' ? this.materials.orange : _deposit.resource === 'siliconOre' ? this.materials.emeraldGreen : this.materials.white
        const accentMaterial = _deposit.resource === 'copperOre' ? this.materials.emeraldGreen : _deposit.resource === 'ironOre' ? this.materials.brown : baseMaterial
        for(let index = 0; index < 32; index++)
        {
            const angle = random() * Math.PI * 2
            const radius = index === 0 ? 0 : 0.7 + random() * 1.7
            const scale = 0.38 + random() * 0.5
            const rock = this.mesh(this.rockGeometry, index % 3 === 0 ? accentMaterial : baseMaterial)
            rock.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius, 0)
            rock.rotation.z = random() * Math.PI * 2
            rock.scale.set(scale * (0.85 + random() * 0.4), scale, scale * (0.7 + random() * 0.55))
            group.add(rock)
        }
        return group
    }

    createItem(_resource, _scale = 1)
    {
        const group = new THREE.Group()
        const materialName = {
            ironOre: 'red', copperOre: 'orange', stone: 'white', coal: 'black', timber: 'brown',
            ironIngot: 'metal', copperIngot: 'copperMetal', ironCog: 'metal', copperWire: 'copperMetal',
            siliconOre: 'emeraldGreen', siliconBar: 'emeraldGreen', greenCircuit: 'emeraldGreen'
        }[_resource]
        const material = this.materials[materialName]

        if(['ironOre', 'copperOre', 'stone', 'coal', 'siliconOre'].includes(_resource))
        {
            const parts = [
                { radius: 0.215, position: [0, 0, 0.22], rotation: [0.2, 0.1, 0.3] },
                { radius: 0.095, position: [-0.19, 0, 0.095], rotation: [0.4, -0.2, 0.1] },
                { radius: 0.095, position: [0.16, -0.13, 0.095], rotation: [-0.2, 0.3, 0.5] },
                { radius: 0.095, position: [0.16, 0.13, 0.095], rotation: [0.25, 0.2, -0.35] }
            ]
            for(const part of parts)
                group.add(this.mesh(new THREE.DodecahedronGeometry(part.radius, 0), material, part.position, part.rotation))
        }
        else if(_resource === 'timber')
        {
            const logGeometry = new THREE.CylinderGeometry(0.11, 0.11, 0.38, 12)
            group.add(this.mesh(logGeometry, [material, this.materials.beige, this.materials.beige], [0, 0, 0.11]))
            const growthRingGeometry = new THREE.TorusGeometry(0.055, 0.008, 4, 12)
            for(const side of [-1, 1])
                group.add(this.mesh(growthRingGeometry, material, [0, side * 0.191, 0.11], [Math.PI * 0.5, 0, 0]))
        }
        else if(_resource === 'ironIngot' || _resource === 'copperIngot' || _resource === 'siliconBar')
        {
            const ingot = this.mesh(this.box(0.42, 0.25, 0.16, 0.045), material, [0, 0, 0.08])
            group.add(ingot)
        }
        else if(_resource === 'ironCog')
        {
            const shape = new THREE.Shape()
            const toothCount = 8
            const pointsPerTooth = 4
            const toothOffsets = [0, 0.2, 0.45, 0.65]
            for(let index = 0; index < toothCount * pointsPerTooth; index++)
            {
                const pointIndex = index % pointsPerTooth
                const angle = (Math.floor(index / pointsPerTooth) + toothOffsets[pointIndex]) / toothCount * Math.PI * 2
                const radius = pointIndex === 1 || pointIndex === 2 ? 0.25 : 0.19
                const x = Math.cos(angle) * radius
                const y = Math.sin(angle) * radius
                if(index === 0) shape.moveTo(x, y)
                else shape.lineTo(x, y)
            }
            shape.closePath()
            const hole = new THREE.Path()
            hole.absarc(0, 0, 0.07, 0, Math.PI * 2, true)
            shape.holes.push(hole)
            group.add(this.mesh(new THREE.ExtrudeGeometry(shape, { depth: 0.1, bevelEnabled: true, bevelSegments: 1, bevelSize: 0.012, bevelThickness: 0.012 }), material))
        }
        else if(_resource === 'copperWire')
        {
            const coilPoints = []
            for(let index = 0; index <= 64; index++)
            {
                const progress = index / 64
                const angle = progress * Math.PI * 4
                coilPoints.push(new THREE.Vector3(Math.cos(angle) * 0.17, Math.sin(angle) * 0.17, 0.08 + progress * 0.16))
            }
            const coilCurve = new THREE.CatmullRomCurve3(coilPoints)
            group.add(this.mesh(new THREE.TubeGeometry(coilCurve, 64, 0.045, 6, false), material))
            for(const progress of [0, 1])
            {
                const cap = this.mesh(new THREE.CircleGeometry(0.045, 6), material)
                const tangent = coilCurve.getTangent(progress)
                if(progress === 0)
                    tangent.negate()
                cap.position.copy(coilCurve.getPoint(progress))
                cap.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), tangent)
                cap.rotateZ(Math.PI / 6)
                group.add(cap)
            }
        }
        else if(_resource === 'greenCircuit')
        {
            group.add(this.mesh(this.box(0.44, 0.34, 0.055, 0.018), material, [0, 0, 0.03]))
            group.add(this.mesh(this.box(0.16, 0.14, 0.055, 0.012), this.materials.black, [0.03, 0, 0.08]))
            for(const side of [-1, 1])
            {
                group.add(this.mesh(this.box(0.04, 0.26, 0.025, 0.006), this.materials.metal, [side * 0.15, 0, 0.07]))
                group.add(this.mesh(this.box(0.1, 0.035, 0.025, 0.006), this.materials.metal, [side * 0.09, 0.1, 0.07]))
            }
        }
        group.scale.setScalar(_scale)
        return group
    }

    createEngineer()
    {
        const group = new THREE.Group()
        const body = new THREE.Group()
        const navy = this.materials.gray.clone()
        navy.name = 'shadeMinerNavy'
        navy.uniforms.diffuse.value.set('#38547a')
        group.add(body)
        const torso = this.mesh(this.capsule(0.34, 0.12, 24), this.materials.orange, [0, 0, 1.01])
        torso.scale.set(1.04, 0.72, 1)
        body.add(torso)
        const waist = this.mesh(this.cylinder(0.35, 0.35, 0.2, 32), navy, [0, 0, 0.67])
        waist.scale.y = 0.7
        body.add(waist)
        const waistBottomGeometry = new THREE.SphereGeometry(0.35, 32, 16, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.5)
        waistBottomGeometry.rotateX(Math.PI * 0.5)
        const waistBottom = this.mesh(waistBottomGeometry, navy, [0, 0, 0.57])
        waistBottom.scale.set(1, 0.7, 0.5)
        body.add(waistBottom)
        body.add(this.mesh(this.box(0.14, 0.045, 0.09, 0.015, 4), this.materials.metal, [0, 0.265, 0.67]))
        body.add(this.mesh(this.box(0.022, 0.025, 0.53, 0.008, 3), this.materials.black, [0, 0.262, 1.02]))
        const shirtPoint = (_x, _z, _side) =>
        {
            const localX = _x / 1.04
            const localZ = _z - 1.01
            const coreZ = THREE.MathUtils.clamp(localZ, -0.06, 0.06)
            const radius = Math.sqrt(Math.max(0.34 ** 2 - (localZ - coreZ) ** 2, 0))
            const localY = _side * Math.sqrt(Math.max(radius ** 2 - localX ** 2, 0))
            const normal = new THREE.Vector3(localX / 1.04, localY / 0.72, localZ - coreZ).normalize()
            const position = new THREE.Vector3(_x, localY * 0.72, _z).addScaledVector(normal, 0.008)
            return { position, normal }
        }
        const shirtPatch = (_columns, _rows, _sample, _flip = false) =>
        {
            const positions = []
            const normals = []
            const indices = []
            for(let row = 0; row <= _rows; row++)
            {
                for(let column = 0; column <= _columns; column++)
                {
                    const { position, normal } = _sample(column / _columns, row / _rows)
                    positions.push(position.x, position.y, position.z)
                    normals.push(normal.x, normal.y, normal.z)
                }
            }
            for(let row = 0; row < _rows; row++)
            {
                for(let column = 0; column < _columns; column++)
                {
                    const a = row * (_columns + 1) + column
                    const b = a + 1
                    const c = a + _columns + 1
                    const d = c + 1
                    if(_flip)
                        indices.push(a, c, b, b, c, d)
                    else
                        indices.push(a, b, c, b, d, c)
                }
            }
            const geometry = new THREE.BufferGeometry()
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
            geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3))
            geometry.setIndex(indices)
            return geometry
        }
        const horizontalStrip = (_bottom, _top) => shirtPatch(64, 2, (_u, _v) =>
        {
            const z = THREE.MathUtils.lerp(_bottom, _top, _v)
            const localZ = z - 1.01
            const coreZ = THREE.MathUtils.clamp(localZ, -0.06, 0.06)
            const radius = Math.sqrt(Math.max(0.34 ** 2 - (localZ - coreZ) ** 2, 0))
            const angle = _u * Math.PI * 2
            return shirtPoint(Math.cos(angle) * radius * 1.04, z, Math.sin(angle) < 0 ? -1 : 1)
        })
        body.add(this.mesh(horizontalStrip(0.874, 0.926), this.materials.white))
        body.add(this.mesh(horizontalStrip(1.0325, 1.0775), this.materials.white))
        for(const side of [-1, 1])
        {
            for(const face of [-1, 1])
            {
                const strip = shirtPatch(4, 16, (_u, _v) =>
                {
                    const z = THREE.MathUtils.lerp(1.045, 1.36, _v)
                    const centerX = side * (0.19 - (z - 1.045) * 0.15)
                    return shirtPoint(centerX + (_u - 0.5) * 0.065, z, face)
                }, face === 1)
                body.add(this.mesh(strip, this.materials.white))
            }
            body.add(this.mesh(this.box(0.15, 0.09, 0.09, 0.025, 4), navy, [side * 0.105, 0.18, 1.35], [side * 0.2, 0, side * 0.25]))
        }

        const head = new THREE.Group()
        head.position.set(0, 0.03, 1.49)
        head.add(this.mesh(new THREE.SphereGeometry(0.31, 28, 20), this.materials.beige))
        head.add(this.mesh(new THREE.SphereGeometry(0.055, 16, 12), this.materials.beige, [0, 0.31, -0.01]))
        for(const side of [-1, 1])
        {
            head.add(this.mesh(new THREE.SphereGeometry(0.032, 12, 10), this.materials.black, [side * 0.105, 0.278, 0.055]))
            const ear = this.mesh(new THREE.SphereGeometry(0.065, 16, 12), this.materials.beige, [side * 0.295, 0, -0.015])
            ear.scale.set(0.55, 0.75, 1)
            head.add(ear)
        }
        const helmetGeometry = new THREE.SphereGeometry(0.38, 32, 20, 0, Math.PI * 2, 0, Math.PI * 0.5)
        helmetGeometry.rotateX(Math.PI * 0.5)
        const helmet = this.mesh(helmetGeometry, this.materials.white, [0, 0, 0.14])
        helmet.scale.z = 0.8
        head.add(helmet)
        head.add(this.mesh(this.cylinder(0.42, 0.42, 0.055, 32), this.materials.white, [0, 0.025, 0.14]))
        head.add(this.mesh(this.box(0.075, 0.52, 0.075, 0.025, 4), this.materials.white, [0, 0, 0.46]))
        const helmetStrap = this.mesh(new THREE.TorusGeometry(0.365, 0.018, 8, 32), navy, [0, 0, 0.205])
        helmetStrap.scale.y = 0.94
        head.add(helmetStrap)
        head.add(this.mesh(this.box(0.17, 0.055, 0.12, 0.025, 4), this.materials.black, [0, 0.354, 0.27]))
        head.add(this.mesh(this.cylinder(0.09, 0.09, 0.12, 24), this.materials.metal, [0, 0.405, 0.285], [Math.PI * 0.5, 0, 0]))
        head.add(this.mesh(this.cylinder(0.064, 0.064, 0.018, 24), this.materials.white, [0, 0.474, 0.285], [Math.PI * 0.5, 0, 0]))
        body.add(head)

        body.add(this.mesh(this.box(0.56, 0.27, 0.55, 0.1, 4), navy, [0, -0.36, 1.03]))
        body.add(this.mesh(this.box(0.48, 0.06, 0.2, 0.035, 4), this.materials.gray, [0, -0.51, 1.18]))
        for(const side of [-1, 1])
            body.add(this.mesh(this.box(0.07, 0.07, 0.52, 0.025, 4), this.materials.black, [side * 0.25, -0.24, 1.03]))

        const legs = []
        for(const side of [-1, 1])
        {
            const pivot = new THREE.Group()
            pivot.position.set(side * 0.19, 0, 0.66)
            pivot.add(this.mesh(this.capsule(0.13, 0.29, 20), navy, [0, 0, -0.27]))
            pivot.add(this.mesh(this.box(0.24, 0.34, 0.18, 0.055, 4), this.materials.brown, [0, 0.08, -0.565]))
            pivot.add(this.mesh(this.box(0.225, 0.13, 0.075, 0.03, 4), this.materials.gray, [0, 0.19, -0.55]))
            pivot.add(this.mesh(this.box(0.245, 0.35, 0.045, 0.018, 4), this.materials.black, [0, 0.08, -0.65]))
            body.add(pivot)
            legs.push(pivot)
        }

        const arms = []
        for(const side of [-1, 1])
        {
            const pivot = new THREE.Group()
            pivot.position.set(side * 0.4, 0, 1.2)
            pivot.add(this.mesh(this.capsule(0.12, 0.12, 20), this.materials.orange, [0, 0, -0.135]))
            pivot.add(this.mesh(this.capsule(0.105, 0.12, 20), navy, [0, 0, -0.385]))
            pivot.add(this.mesh(this.cylinder(0.119, 0.119, 0.05, 20), this.materials.white, [0, 0, -0.27]))
            pivot.add(this.mesh(new THREE.SphereGeometry(0.115, 18, 14), this.materials.beige, [0, 0, -0.535]))
            body.add(pivot)
            arms.push(pivot)
        }

        const pickaxe = this.createTool(false)
        const axe = this.createTool(true)
        arms[1].add(pickaxe, axe)
        const inventory = new THREE.Group()
        inventory.position.set(0, -0.42, 1.12)
        body.add(inventory)
        group.userData = { body, head, legs, arms, pickaxe, axe, inventory }
        return group
    }

    createTool(_axe)
    {
        const tool = new THREE.Group()
        tool.position.set(0, 0.04, -0.54)
        tool.rotation.x = -0.25
        tool.add(this.mesh(this.cylinder(0.045, 0.055, 0.84, 8), this.materials.brown, [0, 0, -0.2]))
        for(const z of [0.04, 0.12])
            tool.add(this.mesh(this.cylinder(0.057, 0.057, 0.035, 8), this.materials.black, [0, 0, z]))
        tool.add(this.mesh(this.cylinder(0.052, 0.052, 0.07, 8), this.materials.gray, [0, 0, 0.22]))
        if(_axe)
        {
            const axeHead = new THREE.Group()
            axeHead.rotation.z = Math.PI
            axeHead.add(this.mesh(this.box(0.11, 0.48, 0.3, 0.045), this.materials.metal, [0, 0.17, -0.56]))
            axeHead.add(this.mesh(this.box(0.115, 0.07, 0.32, 0.02), this.materials.white, [0, 0.4, -0.56]))
            tool.add(axeHead)
        }
        else
        {
            const headShape = new THREE.Shape()
            headShape.moveTo(-0.6, -0.1)
            headShape.lineTo(-0.42, 0.015)
            headShape.lineTo(-0.22, 0.065)
            headShape.lineTo(0, 0.075)
            headShape.lineTo(0.22, 0.065)
            headShape.lineTo(0.42, 0.015)
            headShape.lineTo(0.6, -0.1)
            headShape.lineTo(0.42, -0.035)
            headShape.lineTo(0.22, -0.065)
            headShape.lineTo(0, -0.07)
            headShape.lineTo(-0.22, -0.065)
            headShape.lineTo(-0.42, -0.035)
            headShape.closePath()
            const headGeometry = new THREE.ExtrudeGeometry(headShape, {
                depth: 0.09,
                steps: 1,
                bevelEnabled: true,
                bevelSegments: 1,
                bevelSize: 0.015,
                bevelThickness: 0.015
            })
            headGeometry.applyMatrix4(new THREE.Matrix4().set(
                0, 0, -1, 0.045,
                1, 0, 0, 0,
                0, -1, 0, -0.56,
                0, 0, 0, 1
            ))
            tool.add(this.mesh(headGeometry, this.materials.metal))
            tool.add(this.mesh(this.cylinder(0.09, 0.09, 0.18, 8), this.materials.gray, [0, 0, -0.56]))
        }
        tool.visible = false
        return tool
    }

    createBin()
    {
        const group = new THREE.Group()
        group.add(this.mesh(this.box(2.2, 1.8, 0.24, 0.06), this.materials.gray, [0, 0, 0.14]))
        group.add(this.mesh(this.box(1.9, 1.5, 0.12, 0.03), this.materials.brown, [0, 0, 0.31]))
        for(const side of [-1, 1])
        {
            group.add(this.mesh(this.box(0.18, 1.8, 1.05, 0.04), this.materials.brown, [side * 1.01, 0, 0.82]))
            group.add(this.mesh(this.box(2.0, 0.18, 1.05, 0.04), this.materials.brown, [0, side * 0.81, 0.82]))
            group.add(this.mesh(this.box(0.24, 1.9, 0.12, 0.03), this.materials.metal, [side * 1.01, 0, 1.37]))
            group.add(this.mesh(this.box(2.12, 0.24, 0.12, 0.03), this.materials.metal, [0, side * 0.81, 1.37]))
        }
        for(const x of [-0.78, 0.78])
        {
            for(const y of [-0.93, 0.93])
                group.add(this.mesh(this.box(0.14, 0.1, 1.0, 0.025), this.materials.metal, [x, y, 0.82]))
        }
        const contents = new THREE.Group()
        contents.position.z = 0.38
        group.add(contents)
        group.userData = { contents }
        return group
    }

    createPortBay(_port)
    {
        const group = new THREE.Group()
        const size = TRAY_SIZES.port
        group.position.set(_port.x, _port.y, _port.z)
        group.add(this.mesh(this.box(size.width, size.depth, 0.1, 0.05), this.materials.metal, [0, 0, 0]))
        group.add(this.mesh(this.box(0.84, 0.64, 0.09, 0.035), this.materials.black, [0, 0, 0.055]))
        const contents = new THREE.Group()
        contents.position.z = 0.1
        group.add(contents)
        group.userData.contents = contents
        return group
    }

    createMachine(_type)
    {
        const group = new THREE.Group()
        group.add(this.mesh(this.box(2.2, 1.8, 0.28, 0.08), this.materials.gray, [0, 0, 0.16]))
        const movingPart = new THREE.Group()
        const fire = new THREE.Group()
        const smoke = new THREE.Group()
        let drillBit = null
        let piston = null
        let flywheel = null
        let beam = null
        group.add(movingPart, fire, smoke)

        if(_type.includes('Smelter'))
        {
            group.add(this.mesh(this.box(1.55, 1.35, 1.25, 0.12), this.materials.beige, [0, 0, 0.91]))
            group.add(this.mesh(this.box(0.82, 0.08, 0.56, 0.06), this.materials.black, [0, -0.69, 0.72]))
            fire.add(this.mesh(new THREE.OctahedronGeometry(0.25, 0), this.materials.orange, [0, -0.77, 0.7], [0, 0, 0.4]))
            group.add(this.mesh(this.cylinder(0.25, 0.32, 1.05, 10), this.materials.gray, [0.42, 0.25, 1.9]))
            group.add(this.mesh(this.cylinder(0.34, 0.34, 0.12, 10), this.materials.metal, [0.42, 0.25, 2.44]))
            group.add(this.mesh(this.box(0.28, 1.55, 0.2, 0.04), this.materials.orange, [-0.6, 0, 1.12]))
            for(let index = 0; index < 3; index++)
            {
                const puff = this.mesh(new THREE.DodecahedronGeometry(0.16 + index * 0.04, 0), new THREE.MeshBasicMaterial({ color: 0xffe1c1, transparent: true, opacity: 0.45 }), [0.42, 0.25, 2.7 + index * 0.35])
                puff.userData.offset = index * 1.7
                smoke.add(puff)
            }
        }
        else if(_type.includes('Fabricator'))
        {
            for(const side of [-1, 1])
                group.add(this.mesh(this.box(0.25, 1.35, 1.55, 0.05), this.materials.orange, [side * 0.78, 0, 1.0]))
            group.add(this.mesh(this.box(1.85, 1.4, 0.25, 0.06), this.materials.metal, [0, 0, 1.75]))
            movingPart.add(this.mesh(this.box(1.1, 0.9, 0.24, 0.05), this.materials.gray, [0, 0, 1.22]))
            movingPart.add(this.mesh(this.cylinder(0.15, 0.15, 0.65, 10), this.materials.metal, [0, 0, 1.58]))
            group.add(this.mesh(this.box(1.2, 0.85, 0.18, 0.04), this.materials.black, [0, 0, 0.54]))
            const cog = this.createItem('ironCog', 2.2)
            cog.position.set(0.92, -0.15, 0.95)
            cog.rotation.x = Math.PI * 0.5
            movingPart.add(cog)
        }
        else
        {
            group.add(this.mesh(this.box(1.25, 1.15, 0.8, 0.12), this.materials.orange, [-0.15, 0, 0.7]))
            group.add(this.mesh(this.box(0.82, 0.08, 0.42, 0.05), this.materials.black, [-0.25, -0.59, 0.68]))
            group.add(this.mesh(this.cylinder(0.26, 0.32, 0.85, 12), this.materials.black, [-0.25, 0.2, 1.4]))
            group.add(this.mesh(this.cylinder(0.34, 0.34, 0.12, 12), this.materials.metal, [-0.25, 0.2, 1.86]))

            piston = new THREE.Group()
            piston.position.set(0.65, -0.55, 1.25)
            piston.add(this.mesh(this.cylinder(0.13, 0.13, 1.05, 10), this.materials.metal))
            piston.add(this.mesh(this.cylinder(0.28, 0.28, 0.18, 12), this.materials.gray, [0, 0, 0.42]))
            group.add(piston)

            drillBit = new THREE.Group()
            drillBit.position.set(0.65, -0.55, 0.45)
            drillBit.add(this.mesh(this.cylinder(0.34, 0, 0.75, 10), this.materials.metal))
            const flutePoints = []
            for(let index = 0; index <= 96; index++)
            {
                const progress = index / 96
                const angle = progress * Math.PI * 6
                const radius = 0.32 * (1 - progress) + 0.015
                flutePoints.push(new THREE.Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0.32 - progress * 0.66))
            }
            drillBit.add(this.mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(flutePoints), 96, 0.035, 6, false), this.materials.gray))
            group.add(drillBit)

            beam = new THREE.Group()
            beam.position.set(0.45, -0.55, 2.03)
            beam.add(this.mesh(this.box(1.35, 0.2, 0.18, 0.045), this.materials.gray))
            beam.add(this.mesh(this.cylinder(0.12, 0.12, 0.3, 10), this.materials.metal, [0, 0, 0], [0, Math.PI * 0.5, 0]))
            group.add(beam)

            flywheel = new THREE.Group()
            flywheel.position.set(0.82, -0.05, 1.05)
            flywheel.add(this.mesh(new THREE.TorusGeometry(0.43, 0.065, 8, 20), this.materials.metal, [0, 0, 0], [0, Math.PI * 0.5, 0]))
            flywheel.add(this.mesh(this.box(0.09, 0.74, 0.09, 0.025), this.materials.gray))
            flywheel.add(this.mesh(this.box(0.09, 0.09, 0.74, 0.025), this.materials.gray))
            flywheel.add(this.mesh(this.cylinder(0.12, 0.12, 0.24, 10), this.materials.orange, [0, 0, 0], [0, Math.PI * 0.5, 0]))
            group.add(flywheel)

            for(const side of [-1, 1])
            {
                group.add(this.mesh(this.box(0.18, 1.65, 0.18, 0.03), this.materials.gray, [side * 0.78, 0, 0.45], [side * 0.35, 0, 0]))
                group.add(this.mesh(this.box(1.65, 0.18, 0.18, 0.03), this.materials.gray, [0, side * 0.68, 0.45], [0, side * 0.35, 0]))
            }
            fire.add(this.mesh(new THREE.OctahedronGeometry(0.22, 0), this.materials.orange, [-0.25, -0.67, 0.7], [0, 0, 0.4]))
            fire.add(this.mesh(new THREE.OctahedronGeometry(0.13, 0), this.materials.yellow, [-0.25, -0.72, 0.72]))
            for(let index = 0; index < 3; index++)
            {
                const puff = this.mesh(new THREE.DodecahedronGeometry(0.15 + index * 0.04, 0), new THREE.MeshBasicMaterial({ color: 0xffe1c1, transparent: true, opacity: 0.45 }), [-0.25, 0.2, 2.1 + index * 0.3])
                puff.userData.offset = index * 1.7
                puff.userData.baseX = -0.25
                puff.userData.baseZ = 2.05
                smoke.add(puff)
            }
        }

        const definition = MACHINE_TYPES[_type]
        const portLayout = getMachinePortLayout(_type)
        const inputs = {}
        for(const [resource, port] of Object.entries(portLayout.inputs))
        {
            const bay = this.createPortBay(port)
            inputs[resource] = bay.userData.contents
            group.add(bay)
        }
        const input = inputs[definition.inputs?.[0]] ?? new THREE.Group()
        const fuelBay = portLayout.fuel ? this.createPortBay(portLayout.fuel) : null
        const outputBay = portLayout.output ? this.createPortBay(portLayout.output) : null
        const fuel = fuelBay?.userData.contents ?? new THREE.Group()
        const output = outputBay?.userData.contents ?? new THREE.Group()
        if(fuelBay)
            group.add(fuelBay)
        if(outputBay)
            group.add(outputBay)
        let fuelStatus = null
        let exclamationMaterial = null
        if(definition.fuel)
        {
            fuelStatus = new THREE.Group()
            fuelStatus.position.set(portLayout.fuel.x, portLayout.fuel.y, 2.95)
            exclamationMaterial = new THREE.MeshBasicMaterial({ color: 0xffe889, transparent: true, depthTest: false })
            fuelStatus.add(this.mesh(this.box(0.14, 0.14, 0.48, 0.04), exclamationMaterial, [-0.28, 0, 0.34]))
            fuelStatus.add(this.mesh(new THREE.SphereGeometry(0.11, 10, 8), exclamationMaterial, [-0.28, 0, -0.12]))
            const coal = this.createItem('coal', 1.35)
            coal.position.set(0.35, 0, 0.02)
            fuelStatus.add(coal)
            fuelStatus.visible = false
            group.add(fuelStatus)
        }
        fire.visible = false
        smoke.visible = false
        group.userData = { movingPart, fire, smoke, inputs, input, fuel, output, drillBit, piston, flywheel, beam, fuelStatus, exclamationMaterial }
        return group
    }

    createBeltStraight(_length)
    {
        const group = new THREE.Group()
        group.add(this.mesh(this.box(_length, 0.76, 0.1, 0.025), this.materials.black, [0, 0, 0.1]))
        for(const side of [-1, 1])
            group.add(this.mesh(this.box(_length, 0.075, 0.16, 0.02), this.materials.metal, [0, side * 0.39, 0.15]))
        const slats = []
        const count = Math.max(1, Math.ceil(_length / 0.55))
        for(let index = 0; index < count; index++)
        {
            const slat = this.mesh(this.box(0.09, 0.65, 0.065, 0.018), this.materials.gray, [0, 0, 0.18])
            slat.userData.offset = index / count * _length
            group.add(slat)
            slats.push(slat)
        }
        group.userData.slats = slats
        group.userData.length = _length
        return group
    }

    createBeltCorner()
    {
        const group = new THREE.Group()
        group.add(this.mesh(this.box(0.82, 0.82, 0.1, 0.04), this.materials.black, [0, 0, 0.1]))
        group.add(this.mesh(this.box(0.075, 0.88, 0.16, 0.02), this.materials.metal, [0.39, 0, 0.15]))
        group.add(this.mesh(this.box(0.88, 0.075, 0.16, 0.02), this.materials.metal, [0, -0.39, 0.15]))
        group.add(this.mesh(this.box(0.12, 0.12, 0.16, 0.025), this.materials.metal, [-0.39, 0.39, 0.15]))
        return group
    }

    createBeltSplitter()
    {
        const group = new THREE.Group()
        group.add(this.mesh(this.box(0.86, 0.86, 0.1, 0.04), this.materials.black, [0, 0, 0.1]))
        group.add(this.mesh(this.box(0.86, 0.075, 0.16, 0.02), this.materials.metal, [0, -0.39, 0.15]))
        return group
    }

    createBeltPlug()
    {
        const group = new THREE.Group()
        group.add(this.mesh(this.box(0.66, 0.86, 0.18, 0.04), this.materials.metal, [0, 0, 0.13]))
        group.add(this.mesh(this.box(0.48, 0.66, 0.12, 0.03), this.materials.black, [0, 0, 0.23]))
        return group
    }

    createGroundDrop()
    {
        const group = new THREE.Group()
        const size = TRAY_SIZES.ground
        group.add(this.mesh(this.box(size.width, size.depth, 0.16, 0.06), this.materials.metal, [0, 0, 0.1]))
        group.add(this.mesh(this.box(1.12, 0.92, 0.12, 0.04), this.materials.black, [0, 0, 0.2]))
        for(const side of [-1, 1])
            group.add(this.mesh(this.box(1.3, 0.09, 0.36, 0.025), this.materials.gray, [0, side * 0.54, 0.32]))
        const contents = new THREE.Group()
        contents.position.z = 0.27
        group.add(contents)
        group.userData.contents = contents
        return group
    }

    createCoalBot()
    {
        const group = new THREE.Group()
        const body = new THREE.Group()
        body.add(this.mesh(this.box(0.78, 0.68, 0.48, 0.12), this.materials.orange, [0, 0, 0.62]))
        body.add(this.mesh(this.box(0.58, 0.5, 0.12, 0.04), this.materials.gray, [0, 0, 0.91]))
        body.add(this.mesh(this.box(0.46, 0.08, 0.16, 0.025), this.materials.black, [0, 0.35, 0.69]))
        for(const side of [-1, 1])
            body.add(this.mesh(new THREE.SphereGeometry(0.055, 10, 8), this.materials.emeraldGreen, [side * 0.13, 0.4, 0.7]))
        const antenna = new THREE.Group()
        antenna.position.set(0.22, 0, 0.97)
        antenna.add(this.mesh(this.cylinder(0.025, 0.025, 0.35, 8), this.materials.metal, [0, 0, 0.16]))
        antenna.add(this.mesh(new THREE.SphereGeometry(0.075, 10, 8), this.materials.red, [0, 0, 0.37]))
        body.add(antenna)
        const wheels = []
        for(const side of [-1, 1])
        {
            for(const end of [-1, 1])
            {
                const wheel = this.mesh(this.cylinder(0.18, 0.18, 0.13, 12), this.materials.black, [side * 0.42, end * 0.23, 0.3], [0, Math.PI * 0.5, 0])
                group.add(wheel)
                wheels.push(wheel)
            }
        }
        const cargo = new THREE.Group()
        cargo.position.set(0, 0, 0.98)
        body.add(cargo)
        group.add(body)
        group.add(this.createShadow(0.9, 0.75))
        group.userData = { body, wheels, cargo, antenna }
        return group
    }

    createPin()
    {
        const group = new THREE.Group()
        if(this.pinGeometry)
        {
            const pin = this.mesh(this.pinGeometry, this.materials.red)
            pin.scale.setScalar(0.78)
            group.add(pin)
        }
        else
        {
            group.add(this.mesh(new THREE.ConeGeometry(0.3, 0.75, 16), this.materials.red, [0, 0, 0.55], [-Math.PI * 0.5, 0, 0]))
            group.add(this.mesh(new THREE.SphereGeometry(0.43, 16, 10), this.materials.red, [0, 0, 1.05]))
            group.add(this.mesh(new THREE.SphereGeometry(0.16, 12, 8), this.materials.white, [0.12, -0.36, 1.08]))
        }
        const halo = this.mesh(
            new THREE.TorusGeometry(0.72, 0.075, 8, 32),
            new THREE.MeshBasicMaterial({ color: 0xffe889, transparent: true, opacity: 0.9, depthWrite: false }),
            [0, 0, 0.08]
        )
        halo.renderOrder = 4
        group.add(halo)
        group.userData.halo = halo
        group.add(this.createShadow(0.9, 0.65))
        return group
    }
}
